import { useCallback, useRef, useEffect } from 'react';
import { usePlayheadStore } from '../store/playheadStore';
import { useEditorStore } from '../store/editorStore';
import { pixelToMs, msToPixel } from '../lib/timeUtils';
import { RULER_HEIGHT, getTrackY, getTrackHeight } from '../lib/canvasDrawing';

/**
 * Touch gesture state machine for mobile timeline interaction.
 *
 * Attaches touch listeners with { passive: false } so preventDefault()
 * works — React's onTouchMove is passive by default in modern browsers.
 */

type GestureState =
  | 'IDLE'
  | 'PENDING_TAP'
  | 'PAN_TIMELINE'
  | 'TRIMMING'
  | 'LONG_PRESS_MOVE'
  | 'PINCH_ZOOM'
  | 'SCRUB_RULER';

const TAP_TIMEOUT = 300;
const MOVE_THRESHOLD = 8;
const MOBILE_TRIM_HANDLE_WIDTH = 16;

interface HitResult {
  clipId: string;
  clickMs: number;
  trimHandleSide: 'left' | 'right' | null;
}

export function useMobileTimelineInteraction(canvasRef: React.RefObject<HTMLCanvasElement | null>) {
  const gestureState = useRef<GestureState>('IDLE');
  const tapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTapTime = useRef(0);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const dragClipId = useRef<string | null>(null);
  const dragOffsetMs = useRef(0);
  const trimSide = useRef<'left' | 'right' | null>(null);
  const pinchStartDistance = useRef(0);
  const pinchStartZoom = useRef(0);
  const lastPanX = useRef(0);
  const touchDownHit = useRef<HitResult | null>(null);

  const getTimeFromClientX = useCallback((clientX: number): number => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const { pxPerMs, scrollOffsetMs } = useEditorStore.getState().timeline;
    return Math.max(0, pixelToMs(x, pxPerMs, scrollOffsetMs));
  }, [canvasRef]);

  const getLocalXY = useCallback((touch: Touch): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  }, [canvasRef]);

  const hitTestClip = useCallback((x: number, y: number): HitResult | null => {
    const tracks = useEditorStore.getState().project.tracks;
    const { pxPerMs, scrollOffsetMs, selectedClipId } = useEditorStore.getState().timeline;

    let trackIndex = -1;
    for (let i = 0; i < tracks.length; i++) {
      const ty = getTrackY(tracks, i);
      const th = getTrackHeight(tracks[i]);
      if (y >= ty && y < ty + th) { trackIndex = i; break; }
    }
    if (trackIndex < 0) return null;

    const track = tracks[trackIndex];
    const clickMs = pixelToMs(x, pxPerMs, scrollOffsetMs);

    const clip = track.clips.find(
      (c) => clickMs >= c.startMs && clickMs <= c.startMs + c.durationMs,
    );

    if (!clip) return null;

    let trimHandleSide: 'left' | 'right' | null = null;
    if (clip.id === selectedClipId) {
      const clipXStart = msToPixel(clip.startMs, pxPerMs, scrollOffsetMs);
      const clipXEnd = msToPixel(clip.startMs + clip.durationMs, pxPerMs, scrollOffsetMs);

      if (x >= clipXStart - 4 && x <= clipXStart + MOBILE_TRIM_HANDLE_WIDTH + 4) {
        trimHandleSide = 'left';
      } else if (x >= clipXEnd - MOBILE_TRIM_HANDLE_WIDTH - 4 && x <= clipXEnd + 4) {
        trimHandleSide = 'right';
      }
    }

    return { clipId: clip.id, clickMs, trimHandleSide };
  }, []);

  const getPinchDistance = (touches: TouchList): number => {
    if (touches.length < 2) return 0;
    const dx = touches[1].clientX - touches[0].clientX;
    const dy = touches[1].clientY - touches[0].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Use useEffect to attach non-passive touch listeners directly to the canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    function handleTouchStart(e: TouchEvent) {
      if (e.touches.length >= 2) {
        gestureState.current = 'PINCH_ZOOM';
        pinchStartDistance.current = getPinchDistance(e.touches);
        pinchStartZoom.current = useEditorStore.getState().timeline.pxPerMs;
        if (tapTimer.current) { clearTimeout(tapTimer.current); tapTimer.current = null; }
        return;
      }

      const touch = e.touches[0];
      const { x, y } = getLocalXY(touch);
      touchStartX.current = touch.clientX;
      touchStartY.current = touch.clientY;
      lastPanX.current = touch.clientX;

      if (y < RULER_HEIGHT) {
        const timeMs = getTimeFromClientX(touch.clientX);
        usePlayheadStore.getState().setCurrentTime(timeMs);
        gestureState.current = 'SCRUB_RULER';
        return;
      }

      const hitResult = hitTestClip(x, y);
      touchDownHit.current = hitResult;
      gestureState.current = 'PENDING_TAP';

      tapTimer.current = setTimeout(() => {
        if (gestureState.current !== 'PENDING_TAP') return;

        if (hitResult) {
          gestureState.current = 'LONG_PRESS_MOVE';
          dragClipId.current = hitResult.clipId;
          dragOffsetMs.current = hitResult.clickMs - (() => {
            const tracks = useEditorStore.getState().project.tracks;
            for (const track of tracks) {
              const clip = track.clips.find((c) => c.id === hitResult.clipId);
              if (clip) return clip.startMs;
            }
            return 0;
          })();
          useEditorStore.getState().selectClip(hitResult.clipId);
          if (navigator.vibrate) navigator.vibrate(30);
        }
      }, TAP_TIMEOUT);
    }

    function handleTouchMove(e: TouchEvent) {
      if (gestureState.current === 'PINCH_ZOOM' && e.touches.length >= 2) {
        e.preventDefault();
        const newDistance = getPinchDistance(e.touches);
        if (pinchStartDistance.current === 0) return;
        const scale = newDistance / pinchStartDistance.current;
        const newPxPerMs = Math.min(1.5, Math.max(0.005, pinchStartZoom.current * scale));
        useEditorStore.getState().setZoom(newPxPerMs);
        return;
      }

      if (e.touches.length !== 1) return;
      const touch = e.touches[0];
      const dx = touch.clientX - touchStartX.current;
      const totalMoveX = Math.abs(dx);

      if (gestureState.current === 'SCRUB_RULER') {
        e.preventDefault();
        const timeMs = getTimeFromClientX(touch.clientX);
        usePlayheadStore.getState().setCurrentTime(timeMs);
        return;
      }

      if (gestureState.current === 'PENDING_TAP') {
        if (totalMoveX > MOVE_THRESHOLD) {
          if (tapTimer.current) { clearTimeout(tapTimer.current); tapTimer.current = null; }
          const hit = touchDownHit.current;

          if (hit?.trimHandleSide) {
            gestureState.current = 'TRIMMING';
            dragClipId.current = hit.clipId;
            trimSide.current = hit.trimHandleSide;
            useEditorStore.getState().selectClip(hit.clipId);
            lastPanX.current = touch.clientX;
            if (navigator.vibrate) navigator.vibrate(15);
          } else {
            gestureState.current = 'PAN_TIMELINE';
            lastPanX.current = touch.clientX;
          }
        }
        return;
      }

      if (gestureState.current === 'PAN_TIMELINE') {
        e.preventDefault();
        const deltaX = lastPanX.current - touch.clientX;
        lastPanX.current = touch.clientX;
        const { pxPerMs, scrollOffsetMs } = useEditorStore.getState().timeline;
        const deltaMs = deltaX / pxPerMs;
        useEditorStore.getState().setScrollOffset(Math.max(0, scrollOffsetMs + deltaMs));
        return;
      }

      if (gestureState.current === 'TRIMMING' && dragClipId.current && trimSide.current) {
        e.preventDefault();
        const currentMs = getTimeFromClientX(touch.clientX);
        const lastMs = getTimeFromClientX(lastPanX.current);
        const deltaMs = currentMs - lastMs;
        lastPanX.current = touch.clientX;
        if (deltaMs !== 0) {
          useEditorStore.getState().trimClip(dragClipId.current, trimSide.current, deltaMs);
        }
        return;
      }

      if (gestureState.current === 'LONG_PRESS_MOVE' && dragClipId.current) {
        e.preventDefault();
        const currentMs = getTimeFromClientX(touch.clientX);
        const newStartMs = Math.max(0, currentMs - dragOffsetMs.current);
        useEditorStore.getState().moveClip(dragClipId.current, newStartMs);
        return;
      }
    }

    function handleTouchEnd() {
      if (tapTimer.current) { clearTimeout(tapTimer.current); tapTimer.current = null; }

      if (gestureState.current === 'PENDING_TAP') {
        const now = Date.now();
        const timeSinceLastTap = now - lastTapTime.current;
        const hit = touchDownHit.current;

        if (timeSinceLastTap < TAP_TIMEOUT && hit) {
          useEditorStore.getState().selectClip(hit.clipId);
          useEditorStore.getState().splitClip(hit.clipId, hit.clickMs);
          if (navigator.vibrate) navigator.vibrate(15);
        } else if (hit) {
          useEditorStore.getState().selectClip(hit.clipId);
        } else {
          useEditorStore.getState().selectClip(null);
          const timeMs = getTimeFromClientX(touchStartX.current);
          usePlayheadStore.getState().setCurrentTime(timeMs);
        }

        lastTapTime.current = now;
      }

      gestureState.current = 'IDLE';
      dragClipId.current = null;
      trimSide.current = null;
      touchDownHit.current = null;
    }

    // Attach with { passive: false } so preventDefault() works
    canvas.addEventListener('touchstart', handleTouchStart, { passive: true });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [canvasRef, getLocalXY, getTimeFromClientX, hitTestClip]);
}
